import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { pdfjsLib } from "./pdfWorkerSetup";
import "pdfjs-dist/web/pdf_viewer.css";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Search,
  Download,
  Printer,
  ExternalLink,
  X,
  FileText,
  Sparkles,
  Loader2,
  AlertCircle,
  Maximize2,
  PenLine,
  FileSearch,
  Lightbulb,
  Glasses,
  ClipboardList,
  Gavel,
  MessageSquare,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface PdfJsViewerProps {
  url: string;
  title?: string;
  highlightText?: string | null;
  targetPage?: number | null;
  onClose?: () => void;
  onOpenInNewTab?: () => void;
  onDocumentAction?: (actionPrompt: string) => void;
  isActionsLocked?: boolean;
}

// Helper to normalize strings for robust fuzzy comparison (removes accents, punctuation, extra spaces)
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^\w\s]/g, " ") // replace punctuation with spaces
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim();
}

const SPANISH_STOP_WORDS = new Set([
  "de", "la", "el", "los", "las", "un", "una", "unos", "unas", "y", "o", "en", "a",
  "por", "para", "con", "sin", "sobre", "del", "al", "que", "se", "su", "sus", "como",
  "mas", "pero", "este", "esta", "estos", "estas", "ese", "esa", "esos", "esas",
  "ha", "han", "ser", "sido", "fue", "fueron", "ante", "bajo", "cabe", "contra",
  "desde", "hacia", "hasta", "mediante", "segun", "tras"
]);

const JUDICIAL_BOILERPLATE_WORDS = new Set([
  "corte", "constitucional", "suprema", "justicia", "sala", "plena", "casacion",
  "sentencia", "unificacion", "corporacion", "jurisprudencia", "doctrina",
  "profiere", "resolviendo", "establece", "unifica", "bogota", "expediente",
  "magistrado", "ponente", "referencia", "accion", "tutela"
]);

const isSubstantive = (w: string) => !SPANISH_STOP_WORDS.has(w) && (w.length >= 2 || /\d/.test(w));
const isDeepSubstantive = (w: string) =>
  isSubstantive(w) && !JUDICIAL_BOILERPLATE_WORDS.has(w) && (w.length >= 3 || /\d/.test(w));

interface PageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

export const PdfJsViewer: React.FC<PdfJsViewerProps> = ({
  url,
  title = "Documento",
  highlightText,
  targetPage,
  onClose,
  onOpenInNewTab,
  onDocumentAction,
  isActionsLocked = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const textLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const renderTasksRef = useRef<Map<number, any>>(new Map());

  // PDF Document states
  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageDimensions, setPageDimensions] = useState<PageDimensions[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Citation highlight states
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
  const [citationPage, setCitationPage] = useState<number | null>(null);
  const [isHighlightVisible, setIsHighlightVisible] = useState<boolean>(true);
  const [isSearchingCitation, setIsSearchingCitation] = useState<boolean>(false);

  // Manual search states
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const citationNavigatedKeyRef = useRef<string | null>(null);

  // Pages currently visible / mounted in DOM
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set([1]));

  // AI Actions list matching DocumentActionsPanel
  const aiActions = [
    { label: "Borrador", icon: <PenLine className="h-3.5 w-3.5" />, prompt: "genera un borrador jurídico." },
    { label: "Resumir", icon: <FileSearch className="h-3.5 w-3.5" />, prompt: "resume los puntos clave." },
    { label: "Ideas principales", icon: <Lightbulb className="h-3.5 w-3.5" />, prompt: "extrae las ideas principales." },
    { label: "Análisis preliminar", icon: <Glasses className="h-3.5 w-3.5" />, prompt: "realiza un análisis jurídico preliminar." },
    { label: "Informe detallado", icon: <ClipboardList className="h-3.5 w-3.5" />, prompt: "genera un informe detallado." },
    { label: "Argumentos legales", icon: <Gavel className="h-3.5 w-3.5" />, prompt: "identifica y desarrolla los argumentos legales." },
    { label: "Generar comunicación", icon: <MessageSquare className="h-3.5 w-3.5" />, prompt: "redacta una comunicación formal." },
  ];

  // Keyboard Escape listener to close viewer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // 1. Load the PDF document
  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setErrorMessage(null);
    setPdfDoc(null);
    setNumPages(0);
    setPageDimensions([]);
    pageRefs.current.clear();
    canvasRefs.current.clear();
    textLayerRefs.current.clear();
    renderTasksRef.current.clear();

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: false,
          cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.2.133/cmaps/",
          cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        if (isCancelled) return;

        setPdfDoc(doc);
        setNumPages(doc.numPages);

        // Precompute dimensions of each page
        const dims: PageDimensions[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1.0, rotation: 0 });
          dims.push({
            width: vp.width,
            height: vp.height,
            aspectRatio: vp.width / vp.height,
          });
        }

        if (isCancelled) return;
        setPageDimensions(dims);
        setIsLoading(false);
      } catch (err: any) {
        if (isCancelled) return;
        console.error("Error loading PDF via PDF.js:", err);
        setErrorMessage(err.message || "No se pudo cargar el archivo PDF.");
        setIsLoading(false);
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
    };
  }, [url]);

  // 2. Scan document to find citation page when highlightText changes
  useEffect(() => {
    if (!pdfDoc || !highlightText) {
      setActiveHighlight(null);
      setCitationPage(null);
      return;
    }

    const cleanSnippet = highlightText
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!cleanSnippet) return;

    setActiveHighlight(cleanSnippet);
    setIsHighlightVisible(true);
    setIsSearchingCitation(true);
    citationNavigatedKeyRef.current = null;

    let isCancelled = false;

    const findCitation = async () => {
      const normSnippet = normalizeText(cleanSnippet);
      const snippetWords = normSnippet.split(" ").filter((w) => w.length > 0);

      // Identify legal citation anchor (e.g., "articulo 82", "ley 1564", "c 038")
      const legalRefMatch = normSnippet.match(
        /\b(articulo\s+\d+|art\s+\d+|sentencia\s+[a-z]{1,3}\s*\d+|ley\s+\d+|decreto\s+\d+|acuerdo\s+\d+)/i
      );
      const legalAnchor = legalRefMatch ? legalRefMatch[0].trim() : null;

      // Extract target article number if this citation points to a specific article
      const artMatch = cleanSnippet.match(/\b(?:art[ií]culo|art\.?)\s*(\d+[\w\-]*)/i);
      const targetArtNum = artMatch ? artMatch[1] : null;

      // Helper to test if raw page text contains the actual article definition/header (e.g. "ARTÍCULO 180.", "Art. 180 -")
      const matchesArticleHeading = (rawText: string, artNum: string): boolean => {
        if (!rawText || !artNum) return false;
        const headerPattern = new RegExp(
          `(?:^|[\.\n\r\t])\s*(?:art[ií]culo|art\.?)\s*${artNum}\b(?:\s*[\.:\-–º]|\s+[A-ZÁÉÍÓÚÑ])`,
          "i"
        );
        return headerPattern.test(rawText);
      };

      // Helper to identify if an occurrence is strictly a passing cross-reference (e.g. "en los términos del artículo 180")
      const isPassingReferenceOnly = (rawText: string, artNum: string): boolean => {
        if (!rawText || !artNum) return false;
        const refPattern = new RegExp(
          `\b(?:en|el|del?|al?|seg[uú]n|conforme\s+al?|previsto\s+en|t[eé]rminos\s+del?|art[ií]culos?\s+\d+\s+y)\s+(?:el\s+)?(?:art[ií]culo|art\.?)\s*${artNum}\b`,
          "i"
        );
        return refPattern.test(rawText) && !matchesArticleHeading(rawText, artNum);
      };

      // Extract substantive words (not in stop words, length >= 2 or contains digits)
      const substantiveWords = snippetWords.filter(
        (w) => !SPANISH_STOP_WORDS.has(w) && (w.length >= 2 || /\d/.test(w))
      );
      const deepWords = snippetWords.filter(isDeepSubstantive);
      const scoringWords = deepWords.length >= 4 ? deepWords : substantiveWords;
      const uniqueScoringWords = Array.from(new Set(scoringWords));
      const scoringBigrams: string[] = [];
      for (let b = 0; b < scoringWords.length - 1; b++) {
        scoringBigrams.push(`${scoringWords[b]} ${scoringWords[b + 1]}`);
      }
      const anchorPhrase = (substantiveWords.length > 0 ? substantiveWords : snippetWords)
        .slice(0, Math.min(6, substantiveWords.length || snippetWords.length))
        .join(" ");

      let foundPage: number | null = null;

      // 1. First test if targetPage (> 1, or == 1 in short 1-2 page docs) actually contains the snippet or article heading
      if (targetPage && targetPage >= 1 && targetPage <= pdfDoc.numPages && (targetPage > 1 || pdfDoc.numPages <= 2)) {
        try {
          const page = await pdfDoc.getPage(targetPage);
          const content = await page.getTextContent();
          const rawPageStr = content.items.map((item: any) => item.str || "").join(" ");
          const normPageStr = normalizeText(rawPageStr);

          // If looking for an article, do NOT accept targetPage if it only contains a passing cross-reference
          const isInvalidArtTarget = targetArtNum && isPassingReferenceOnly(rawPageStr, targetArtNum);

          if (!isInvalidArtTarget) {
            if (
              (targetArtNum && matchesArticleHeading(rawPageStr, targetArtNum)) ||
              (normSnippet && normPageStr.includes(normSnippet)) ||
              (anchorPhrase && normPageStr.includes(anchorPhrase))
            ) {
              foundPage = targetPage;
            }
          }
        } catch (e) {
          console.warn(`Error testing targetPage ${targetPage}:`, e);
        }
      }

      // 2. If targetPage didn't contain it or wasn't provided, scan all pages
      if (!foundPage) {
        let bestPage = 1;
        let maxMatches = 0;
        let bestWindowScore = 0;
        let legalAnchorCandidatePage: number | null = null;
        let articleHeadingPage: number | null = null;

        for (let i = 1; i <= pdfDoc.numPages; i++) {
          if (isCancelled) return;
          try {
            const page = await pdfDoc.getPage(i);
            const content = await page.getTextContent();
            const rawPageStr = content.items.map((item: any) => item.str || "").join(" ");
            const normPageStr = normalizeText(rawPageStr);
            if (normPageStr.length < 60 && pdfDoc.numPages > 2) continue;

            // Skip pure endnote/footnote compilation pages (e.g., pages with >= 4 bracketed [112] footnote defs)
            const footnoteDefs = (rawPageStr.match(/(?:^|\s)\[\d{2,3}\]\s+/g) || []).length;
            const isEndnotePage = pdfDoc.numPages > 10 && i > pdfDoc.numPages * 0.75 && footnoteDefs >= 4;

            // 1. Article heading match: absolute highest priority for statutory articles
            if (targetArtNum && matchesArticleHeading(rawPageStr, targetArtNum)) {
              articleHeadingPage = i;
              foundPage = i;
              break;
            }

            // 2. Exact match (ignore if merely an article cross reference)
            if (normSnippet && normPageStr.includes(normSnippet)) {
              if (!targetArtNum || !isPassingReferenceOnly(rawPageStr, targetArtNum)) {
                foundPage = i;
                break;
              } else if (legalAnchorCandidatePage === null) {
                legalAnchorCandidatePage = i;
              }
            }

            // 3. Anchor phrase match (substantive words)
            if (anchorPhrase && anchorPhrase.split(" ").length >= 4 && normPageStr.includes(anchorPhrase) && !isEndnotePage) {
              foundPage = i;
              break;
            }

            // 4. Legal anchor match with substantive confirmation
            if (legalAnchor && normPageStr.includes(legalAnchor)) {
              const substantiveMatches = substantiveWords.filter((w) => normPageStr.includes(w)).length;
              if (substantiveMatches >= 2 || substantiveWords.length <= 1) {
                if (!targetArtNum || !isPassingReferenceOnly(rawPageStr, targetArtNum)) {
                  foundPage = i;
                  break;
                }
              }
              if (legalAnchorCandidatePage === null) {
                legalAnchorCandidatePage = i;
              }
            }

            // 5. Localized paragraph-window density scoring (handles both verbatim chunks and doctrinal summaries)
            if (!isEndnotePage && uniqueScoringWords.length >= 3) {
              const pageWords = normPageStr.split(" ");
              const winSize = 65;
              const step = 25;
              for (let wStart = 0; wStart < pageWords.length; wStart += step) {
                const winSlice = pageWords.slice(wStart, wStart + winSize).join(" ");
                let winScore = 0;
                for (const bg of scoringBigrams) {
                  if (winSlice.includes(bg)) winScore += 4;
                }
                for (const uw of uniqueScoringWords) {
                  if (winSlice.includes(uw)) winScore += 1;
                }
                if (winScore > bestWindowScore) {
                  bestWindowScore = winScore;
                  bestPage = i;
                  maxMatches = winScore;
                }
              }
            }
          } catch (e) {
            console.warn(`Error scanning page ${i} for citation:`, e);
          }
        }

        if (!foundPage && articleHeadingPage !== null) {
          foundPage = articleHeadingPage;
        } else if (!foundPage && maxMatches >= 2) {
          foundPage = bestPage;
        } else if (!foundPage && legalAnchorCandidatePage !== null) {
          foundPage = legalAnchorCandidatePage;
        }
      }

      // Fallback
      if (!foundPage) {
        foundPage = targetPage && targetPage <= pdfDoc.numPages ? targetPage : 1;
      }

      if (isCancelled) return;
      setIsSearchingCitation(false);

      if (foundPage) {
        setCitationPage(foundPage);
        setCurrentPage(foundPage);
        setRenderedPages((prev) => new Set([...prev, foundPage!]));

        const jumpToTargetPage = () => {
          const container = containerRef.current;
          const pageEl = pageRefs.current.get(foundPage!);
          if (container && pageEl) {
            const containerRect = container.getBoundingClientRect();
            const pageRect = pageEl.getBoundingClientRect();
            const targetScrollTop = container.scrollTop + (pageRect.top - containerRect.top);
            // Instant jump: avoids the dizzying multi-page smooth scroll across dozens of pages
            container.scrollTo({ top: Math.max(0, targetScrollTop - 20), behavior: "auto" });
          }
        };

        requestAnimationFrame(() => {
          jumpToTargetPage();
          setTimeout(jumpToTargetPage, 100);
        });
      }
    };

    findCitation();

    return () => {
      isCancelled = true;
    };
  }, [pdfDoc, highlightText, targetPage]);

  // 3. Highlight matching citation or search query inside textLayer spans
  // Uses contiguous token-sequence matching + sentence/paragraph boundary expansion
  // Strictly prevents highlighting isolated words, stop-words or fragmented noise.
  const applyHighlightsToTextLayer = useCallback(
    (pageNumber: number, textLayerDiv: HTMLDivElement) => {
      const spans = Array.from(textLayerDiv.querySelectorAll("span")) as HTMLSpanElement[];
      if (spans.length === 0) return;

      // Clear previous highlights
      spans.forEach((span) => {
        span.classList.remove("pdf-citation-highlight", "pdf-search-highlight");
      });

      let rawTerm =
        (isHighlightVisible && activeHighlight ? activeHighlight : "") ||
        (searchQuery.trim().length > 1 ? searchQuery.trim() : "");

      if (!rawTerm) return;

      // If snippet contains multiple sections joined by [...], take the longest informative fragment
      if (rawTerm.includes("[...]")) {
        const parts = rawTerm.split("[...]").map((p) => p.trim()).filter((p) => p.length > 25);
        if (parts.length > 0) {
          rawTerm = parts.reduce((a, b) => (a.length >= b.length ? a : b));
        }
      }

      const isCitation = Boolean(isHighlightVisible && activeHighlight);
      const normTarget = normalizeText(rawTerm);
      if (!normTarget) return;

      const SPANISH_STOP_WORDS = new Set([
        "de", "la", "el", "los", "las", "un", "una", "unos", "unas", "y", "o", "en", "a",
        "por", "para", "con", "sin", "sobre", "del", "al", "que", "se", "su", "sus", "como",
        "mas", "pero", "este", "esta", "estos", "estas", "ese", "esa", "esos", "esas",
        "ha", "han", "ser", "sido", "fue", "fueron", "ante", "bajo", "cabe", "contra",
        "desde", "hacia", "hasta", "mediante", "segun", "tras"
      ]);

      const targetWords = normTarget
        .split(" ")
        .filter((w) => w.length > 0 && w !== "..." && w !== "[...]");

      if (targetWords.length === 0) return;

      // Extract legal citation anchor (e.g., "articulo 82", "ley 1564")
      const legalRefMatch = normTarget.match(
        /\b(articulo\s+\d+|art\s+\d+|sentencia\s+[a-z]{1,3}\s*\d+|ley\s+\d+|decreto\s+\d+|acuerdo\s+\d+)/i
      );
      const legalAnchor = legalRefMatch ? legalRefMatch[0].trim() : null;

      // Flatten all spans into a token list with back-references to span index
      interface SpanToken {
        word: string;
        spanIndex: number;
        originalText: string;
      }
      const pageTokens: SpanToken[] = [];
      spans.forEach((span, spanIdx) => {
        const rawSpanText = span.textContent || "";
        const normSpan = normalizeText(rawSpanText);
        if (!normSpan) return;
        normSpan.split(" ").filter((w) => w.length > 0).forEach((w) => {
          pageTokens.push({ word: w, spanIndex: spanIdx, originalText: rawSpanText });
        });
      });

      if (pageTokens.length === 0) return;

      const matchedSpanIndices = new Set<number>();
      const targetLen = targetWords.length;
      const pageLen = pageTokens.length;

      // --- Case A: single-word manual search (NOT citations) ---
      if (!isCitation && targetLen === 1) {
        const singleWord = targetWords[0];
        if (singleWord.length >= 2) {
          spans.forEach((span, spanIdx) => {
            const normSpan = normalizeText(span.textContent || "");
            if (!normSpan) return;
            const words = normSpan.split(" ");
            if (words.includes(singleWord) || (singleWord.length >= 4 && normSpan.includes(singleWord))) {
              matchedSpanIndices.add(spanIdx);
            }
          });
        }
      } else {
        // --- Case B: Sequence matching for citations & queries ---
        let bestMatchStart = -1;
        let bestMatchEnd = -1;
        let maxConsecutiveMatches = 0;
        let hasValidMatch = false;

        // 2. Contiguous token sequence matching
        if (!hasValidMatch) {
          for (let i = 0; i < pageLen; i++) {
            let consecutive = 0;
            let tIdx = 0;
            let sIdx = i;

            while (sIdx < pageLen && tIdx < targetLen) {
              if (pageTokens[sIdx].word === targetWords[tIdx]) {
                consecutive++;
                sIdx++;
                tIdx++;
              } else if (
                consecutive >= 2 &&
                sIdx + 1 < pageLen &&
                pageTokens[sIdx + 1].word === targetWords[tIdx]
              ) {
                sIdx += 2;
                tIdx++;
                consecutive++;
              } else if (
                consecutive >= 2 &&
                tIdx + 1 < targetLen &&
                pageTokens[sIdx].word === targetWords[tIdx + 1]
              ) {
                tIdx += 2;
                sIdx++;
                consecutive++;
              } else {
                break;
              }
            }

            if (consecutive > maxConsecutiveMatches) {
              maxConsecutiveMatches = consecutive;
              bestMatchStart = i;
              bestMatchEnd = sIdx - 1;
            }
          }

          // Count substantive words in match
          let matchedSubstantiveCount = 0;
          if (bestMatchStart >= 0 && bestMatchEnd >= bestMatchStart) {
            for (let k = bestMatchStart; k <= bestMatchEnd; k++) {
              if (isSubstantive(pageTokens[k].word)) {
                matchedSubstantiveCount++;
              }
            }
          }

          if (isCitation) {
            if (targetLen <= 3) {
              hasValidMatch = maxConsecutiveMatches >= Math.min(2, targetLen) && matchedSubstantiveCount >= 1;
            } else {
              const reqConsecutive = Math.min(4, Math.max(3, Math.ceil(targetLen * 0.2)));
              hasValidMatch = maxConsecutiveMatches >= reqConsecutive && matchedSubstantiveCount >= 2;
            }
          } else {
            hasValidMatch = maxConsecutiveMatches >= (targetLen <= 3 ? targetLen : Math.min(4, Math.ceil(targetLen * 0.35)));
          }
        }

        // 3. Fallback for citations: check 4-to-6 token anchor phrases
        if (!hasValidMatch && isCitation && targetLen >= 4) {
          const anchors: string[][] = [
            targetWords.slice(0, Math.min(6, targetLen)),
            targetWords.slice(Math.max(0, Math.floor(targetLen / 2) - 2), Math.min(targetLen, Math.floor(targetLen / 2) + 3)),
            targetWords.slice(-Math.min(6, targetLen))
          ].filter(a => a.length >= 3 && a.some(w => isSubstantive(w)));

          for (const anchor of anchors) {
            for (let i = 0; i <= pageLen - anchor.length; i++) {
              let matches = true;
              for (let a = 0; a < anchor.length; a++) {
                if (pageTokens[i + a].word !== anchor[a]) {
                  matches = false;
                  break;
                }
              }
              if (matches) {
                bestMatchStart = i;
                bestMatchEnd = i + anchor.length - 1;
                maxConsecutiveMatches = anchor.length;
                hasValidMatch = true;
                break;
              }
            }
            if (hasValidMatch) break;
          }
        }

        // 4. Legal Article Heading Match (when citing a specific article like "Artículo 405")
        if (!hasValidMatch && isCitation && legalAnchor && /^art(?:iculo)?\s+\d+/i.test(legalAnchor)) {
          const anchorTokens = legalAnchor.split(" ");
          for (let i = 0; i <= pageLen - anchorTokens.length; i++) {
            let match = true;
            for (let a = 0; a < anchorTokens.length; a++) {
              if (pageTokens[i + a].word !== anchorTokens[a]) {
                match = false;
                break;
              }
            }
            if (match) {
              bestMatchStart = i;
              bestMatchEnd = Math.min(pageLen - 1, i + 45);
              maxConsecutiveMatches = anchorTokens.length;
              hasValidMatch = true;
              break;
            }
          }
        }

        // 5. High-Density Substantive Window Fallback (guarantees yellow highlight on citationPage even for synthesized ratio decidendi)
        if (!hasValidMatch && isCitation && pageNumber === citationPage) {
          const deepSet = new Set(targetWords.filter(isDeepSubstantive));
          const fallbackSet = deepSet.size >= 3 ? deepSet : new Set(targetWords.filter(isSubstantive));
          const targetBigrams = new Set<string>();
          const substantiveArr = Array.from(fallbackSet);
          for (let b = 0; b < targetWords.length - 1; b++) {
            if (fallbackSet.has(targetWords[b]) && fallbackSet.has(targetWords[b + 1])) {
              targetBigrams.add(`${targetWords[b]} ${targetWords[b + 1]}`);
            }
          }
          if (substantiveArr.length >= 2) {
            const winLen = Math.min(55, pageLen);
            let bestWinScore = 0;
            let bestWinIdx = -1;
            for (let w = 0; w <= pageLen - winLen; w += 5) {
              let sc = 0;
              for (let k = w; k < w + winLen; k++) {
                const tok = pageTokens[k].word;
                if (fallbackSet.has(tok)) sc += 1;
                if (k + 1 < w + winLen && targetBigrams.has(`${tok} ${pageTokens[k + 1].word}`)) {
                  sc += 3;
                }
              }
              if (sc > bestWinScore) {
                bestWinScore = sc;
                bestWinIdx = w;
              }
            }
            if (bestWinIdx >= 0 && bestWinScore >= 3) {
              bestMatchStart = bestWinIdx;
              bestMatchEnd = Math.min(pageLen - 1, bestWinIdx + winLen - 1);
              hasValidMatch = true;
            }
          }
        }

        if (hasValidMatch && bestMatchStart >= 0 && bestMatchEnd >= bestMatchStart) {
          if (isCitation) {
            // === SENTENCE & PARAGRAPH BOUNDARY EXPANSION ===
            let sentenceStartToken = bestMatchStart;
            let sentenceEndToken = bestMatchEnd;

            const hasSentenceTerminator = (text: string) => {
              const t = text.trim();
              return (t.endsWith(".") && !/^(?:art[ií]culo|art|num|inc|par|cap|tit|no|dr|dra|mp|rad)\s*\d*\.$/i.test(t)) || t.endsWith("!") || t.endsWith("?") || t.endsWith(";");
            };

            // Expand backwards to sentence start (or max 35 tokens)
            for (let b = bestMatchStart - 1; b >= Math.max(0, bestMatchStart - 35); b--) {
              const prevText = pageTokens[b].originalText;
              if (hasSentenceTerminator(prevText)) {
                break;
              }
              sentenceStartToken = b;
            }

            // Expand forwards to sentence end (or max 45 tokens)
            for (let f = bestMatchEnd + 1; f < Math.min(pageLen, bestMatchEnd + 45); f++) {
              sentenceEndToken = f;
              const curText = pageTokens[f].originalText;
              if (hasSentenceTerminator(curText)) {
                break;
              }
            }

            // Highlight all spans covering the complete sentence
            for (let k = sentenceStartToken; k <= sentenceEndToken; k++) {
              if (pageTokens[k]) {
                matchedSpanIndices.add(pageTokens[k].spanIndex);
              }
            }
          } else {
            // For manual search: highlight the exact matched tokens
            for (let k = bestMatchStart; k <= bestMatchEnd; k++) {
              if (pageTokens[k]) {
                matchedSpanIndices.add(pageTokens[k].spanIndex);
              }
            }
          }
        }
      }

      let firstHighlightedSpan: HTMLElement | null = null;
      matchedSpanIndices.forEach((spanIdx) => {
        const span = spans[spanIdx];
        if (span) {
          span.classList.add(isCitation ? "pdf-citation-highlight" : "pdf-search-highlight");
          if (!firstHighlightedSpan) firstHighlightedSpan = span;
        }
      });

      // Navigation to highlighted span is handled exclusively once on page load (not on every highlight re-render)

    },
    [activeHighlight, isHighlightVisible, searchQuery, citationPage]
  );

  // 4. Render page canvas and textLayer
  const renderPage = useCallback(
    async (pageNumber: number) => {
      if (!pdfDoc) return;

      const canvas = canvasRefs.current.get(pageNumber);
      const textLayerDiv = textLayerRefs.current.get(pageNumber);
      if (!canvas) return;

      try {
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale, rotation });

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        // Cancel previous render task on this page if running
        const existingTask = renderTasksRef.current.get(pageNumber);
        if (existingTask) {
          try {
            existingTask.cancel();
          } catch (_) {}
        }

        const renderContext = {
          canvasContext: ctx,
          viewport,
        };

        const renderTask = page.render(renderContext);
        renderTasksRef.current.set(pageNumber, renderTask);
        await renderTask.promise;

        // Render TextLayer
        if (textLayerDiv) {
          textLayerDiv.innerHTML = "";
          textLayerDiv.style.width = `${Math.floor(viewport.width)}px`;
          textLayerDiv.style.height = `${Math.floor(viewport.height)}px`;
          textLayerDiv.style.setProperty("--scale-factor", `${scale}`);

          const textContent = await page.getTextContent();
          const { TextLayer } = pdfjsLib as any;

          if (TextLayer) {
            const textLayer = new TextLayer({
              textContentSource: textContent,
              container: textLayerDiv,
              viewport,
            });
            await textLayer.render();
          }

          // Apply citation or search highlights after textLayer renders
          applyHighlightsToTextLayer(pageNumber, textLayerDiv);

          // Center the citation ONCE upon initial load of the citation page (never during free scrolling)
          const isCitationPage = pageNumber === citationPage;
          const citationKey = `${pageNumber}-${activeHighlight}`;
          if (
            isCitationPage &&
            isHighlightVisible &&
            activeHighlight &&
            citationNavigatedKeyRef.current !== citationKey
          ) {
            citationNavigatedKeyRef.current = citationKey;
            requestAnimationFrame(() => {
              setTimeout(() => {
                const highlighted = textLayerDiv.querySelector(".pdf-citation-highlight") as HTMLElement;
                const container = containerRef.current;
                if (highlighted && container) {
                  const containerRect = container.getBoundingClientRect();
                  const spanRect = highlighted.getBoundingClientRect();
                  const targetScrollTop =
                    container.scrollTop +
                    (spanRect.top - containerRect.top) -
                    (container.clientHeight / 2) +
                    (spanRect.height / 2);
                  container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
                }
              }, 80);
            });
          }
        }
      } catch (err: any) {
        if (err?.name !== "RenderingCancelledException") {
          console.error(`Error rendering page ${pageNumber}:`, err);
        }
      }
    },
    [pdfDoc, scale, rotation, applyHighlightsToTextLayer]
  );

  // Re-apply highlights whenever highlight/search terms or visibility change
  useEffect(() => {
    renderedPages.forEach((pageNum) => {
      const textLayerDiv = textLayerRefs.current.get(pageNum);
      if (textLayerDiv) {
        applyHighlightsToTextLayer(pageNum, textLayerDiv);
      }
    });
  }, [activeHighlight, isHighlightVisible, searchQuery, renderedPages, applyHighlightsToTextLayer]);

  // Re-render visible pages when scale or rotation changes
  useEffect(() => {
    renderedPages.forEach((pageNum) => {
      renderPage(pageNum);
    });
  }, [scale, rotation, renderedPages, renderPage]);

  // 5. Track current page on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 3;

      let closestPage = currentPage;
      let minDistance = Infinity;

      pageRefs.current.forEach((el, pageNum) => {
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.top - centerY);
        if (dist < minDistance) {
          minDistance = dist;
          closestPage = pageNum;
        }

        // Lazy load visible pages
        if (rect.bottom >= containerRect.top - 300 && rect.top <= containerRect.bottom + 300) {
          setRenderedPages((prev) => {
            if (prev.has(pageNum)) return prev;
            const next = new Set(prev);
            next.add(pageNum);
            return next;
          });
        }
      });

      if (closestPage !== currentPage) {
        setCurrentPage(closestPage);
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [currentPage]);

  // Navigation handlers
  const goToPage = (target: number) => {
    const p = Math.max(1, Math.min(numPages, target));
    setCurrentPage(p);
    setRenderedPages((prev) => new Set([...prev, p]));
    const el = pageRefs.current.get(p);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleZoomIn = () => setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)));
  const handleZoomOut = () => setScale((s) => Math.max(0.6, +(s - 0.15).toFixed(2)));
  const handleResetZoom = () => setScale(1.15);
  const handleFitWidth = () => {
    if (!containerRef.current || pageDimensions.length === 0) return;
    const containerWidth = containerRef.current.clientWidth - 48; // padding
    const pageWidth = pageDimensions[0]?.width || 612;
    setScale(+(containerWidth / pageWidth).toFixed(2));
  };

  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = title.endsWith(".pdf") ? title : `${title}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    const printWindow = window.open(url, "_blank");
    if (printWindow) {
      printWindow.focus();
      printWindow.print();
    }
  };

  const scrollToCitation = () => {
    if (citationPage) {
      setIsHighlightVisible(true);
      goToPage(citationPage);
      const textLayerDiv = textLayerRefs.current.get(citationPage);
      if (textLayerDiv && containerRef.current) {
        const highlighted = textLayerDiv.querySelector(".pdf-citation-highlight") as HTMLElement;
        if (highlighted) {
          const container = containerRef.current;
          const containerRect = container.getBoundingClientRect();
          const spanRect = highlighted.getBoundingClientRect();
          const targetScrollTop =
            container.scrollTop +
            (spanRect.top - containerRect.top) -
            (container.clientHeight / 2) +
            (spanRect.height / 2);
          container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
          return;
        }
      }
      const pageEl = pageRefs.current.get(citationPage);
      if (pageEl && containerRef.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageEl.getBoundingClientRect();
        const targetScrollTop = container.scrollTop + (pageRect.top - containerRect.top);
        container.scrollTo({ top: Math.max(0, targetScrollTop - 20), behavior: "smooth" });
      }
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-100 relative overflow-hidden select-none">
      {/* Dynamic CSS styles for PDF textLayer and citation highlights */}
      <style>{`
        .pdf-page-container {
          position: relative;
          margin: 14px auto;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.04);
          background-color: #ffffff;
          border-radius: 6px;
          overflow: hidden;
        }
        .pdf-page-container canvas {
          display: block;
        }
        .textLayer {
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          opacity: 1;
          line-height: 1;
          z-index: 2;
        }
        .textLayer :is(span, br) {
          color: transparent !important;
          position: absolute;
          white-space: pre;
          cursor: text;
          transform-origin: 0% 0%;
        }
        .textLayer ::selection {
          background: rgba(59, 130, 246, 0.35) !important;
        }
        @keyframes citationPulse {
          0% {
            background-color: rgba(250, 204, 21, 0.8) !important;
            box-shadow: 0 0 12px rgba(245, 158, 11, 0.7) !important;
          }
          50% {
            background-color: rgba(250, 204, 21, 0.35) !important;
            box-shadow: 0 0 3px rgba(245, 158, 11, 0.2) !important;
          }
          100% {
            background-color: rgba(250, 204, 21, 0.5) !important;
            box-shadow: 0 1px 4px rgba(245, 158, 11, 0.35) !important;
          }
        }
        .pdf-citation-highlight {
          background-color: rgba(250, 204, 21, 0.45) !important;
          mix-blend-mode: multiply !important;
          border-radius: 3px !important;
          border-bottom: 2px solid rgba(217, 119, 6, 0.95) !important;
          box-shadow: 0 1px 3px rgba(245, 158, 11, 0.22) !important;
          z-index: 5 !important;
          animation: citationPulse 1.8s ease-in-out 3 !important;
          transition: background-color 0.2s ease !important;
        }
        .pdf-citation-highlight:hover {
          background-color: rgba(250, 204, 21, 0.6) !important;
        }
        .pdf-search-highlight {
          background-color: rgba(147, 197, 253, 0.38) !important;
          mix-blend-mode: multiply !important;
          border-radius: 3px !important;
          border-bottom: 2px solid rgba(37, 99, 235, 0.85) !important;
          z-index: 5 !important;
        }
      `}</style>

      {/* Top Header Bar */}
      <div className="flex-none px-4 py-3 bg-white border-b border-slate-200/80 flex items-center justify-between z-20 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <div className="flex items-center gap-2.5 overflow-hidden flex-1 min-w-0 mr-3">
          <div className="p-2 bg-blue-50 border border-blue-100 text-blue-600 rounded-xl shrink-0 shadow-xs">
            <FileText className="h-4 w-4" />
          </div>
          <div className="flex flex-col min-w-0">
            <h3 className="text-xs font-bold text-slate-800 truncate leading-tight" title={title}>
              {title}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Visor PDF.js
              </span>
              {numPages > 0 && (
                <span className="text-[10px] text-slate-400 font-medium">
                  • {numPages} {numPages === 1 ? "página" : "páginas"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Quick AI Actions Dropdown */}
          {onDocumentAction && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isActionsLocked}
                  className="h-8 px-2.5 rounded-xl border-blue-200 bg-blue-50/50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-bold text-xs gap-1.5 transition-all shadow-2xs"
                >
                  <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                  <span className="hidden sm:inline">Acciones IA</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-1.5 rounded-2xl shadow-xl border-slate-200">
                <DropdownMenuLabel className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 px-2 py-1">
                  Acciones sobre el documento
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-1" />
                {aiActions.map((action, idx) => (
                  <DropdownMenuItem
                    key={idx}
                    onClick={() => onDocumentAction(action.prompt)}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-slate-700 hover:text-blue-700 hover:bg-blue-50 cursor-pointer"
                  >
                    <span className="text-blue-600">{action.icon}</span>
                    <span>{action.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {onOpenInNewTab && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onOpenInNewTab}
              title="Abrir en nueva pestaña"
              className="h-8 w-8 rounded-xl text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}

          <div className="h-4 w-px bg-slate-200 mx-0.5" />

          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              title="Cerrar visor de citas (Esc)"
              className="h-8 px-2.5 rounded-xl border-slate-200 hover:border-red-300 bg-white hover:bg-red-50 text-slate-600 hover:text-red-600 font-bold text-xs gap-1.5 transition-all shadow-2xs group/close shrink-0"
            >
              <X className="h-3.5 w-3.5 text-slate-500 group-hover/close:text-red-600 transition-colors" />
              <span>Cerrar</span>
            </Button>
          )}
        </div>
      </div>

      {/* Secondary Controls Toolbar */}
      <div className="flex-none px-3 py-2 bg-slate-50/90 backdrop-blur-xs border-b border-slate-200/60 flex items-center justify-between gap-2 z-10 overflow-x-auto no-scrollbar">
        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            disabled={currentPage <= 1 || isLoading}
            onClick={() => goToPage(currentPage - 1)}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white disabled:opacity-30"
            title="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1 text-xs font-semibold text-slate-600 px-1">
            <span>Pág.</span>
            <input
              type="number"
              min={1}
              max={numPages || 1}
              value={currentPage}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) goToPage(val);
              }}
              className="w-10 h-6 text-center text-xs font-bold bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-500 shadow-2xs"
            />
            <span className="text-slate-400 font-normal">de {numPages || 1}</span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            disabled={currentPage >= numPages || isLoading}
            onClick={() => goToPage(currentPage + 1)}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white disabled:opacity-30"
            title="Página siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom & View Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            disabled={isLoading || scale <= 0.6}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
            title="Alejar (-)"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>

          <button
            onClick={handleResetZoom}
            className="text-[11px] font-bold text-slate-600 hover:text-blue-600 px-1.5 py-0.5 rounded hover:bg-white transition-colors"
            title="Restablecer zoom al 100%"
          >
            {Math.round(scale * 100)}%
          </button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            disabled={isLoading || scale >= 2.5}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
            title="Acercar (+)"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleFitWidth}
            disabled={isLoading}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
            title="Ajustar al ancho"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleRotate}
            disabled={isLoading}
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
            title="Girar 90°"
          >
            <RotateCw className="h-3.5 w-3.5" />
          </Button>

          <div className="h-4 w-px bg-slate-200 mx-1"></div>

          {/* Search Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setSearchOpen(!searchOpen);
              if (!searchOpen) {
                setTimeout(() => searchInputRef.current?.focus(), 100);
              }
            }}
            className={`h-7 w-7 rounded-lg transition-colors ${
              searchOpen ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-white"
            }`}
            title="Buscar en documento"
          >
            <Search className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrint}
            title="Imprimir documento"
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
          >
            <Printer className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            title="Descargar archivo"
            className="h-7 w-7 rounded-lg text-slate-600 hover:bg-white"
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Inline Search Bar */}
      {searchOpen && (
        <div className="flex-none px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-2 text-xs animate-in slide-in-from-top-2 duration-200 z-10 shadow-xs">
          <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar palabras o artículos en el texto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 text-xs bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Active Citation Highlight Banner */}
      {activeHighlight && (
        <div className="flex-none px-4 py-2 bg-gradient-to-r from-amber-50 via-amber-100/60 to-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 text-xs text-amber-950 animate-in fade-in slide-in-from-top-1 duration-300 z-10 shadow-2xs">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="p-1 bg-amber-200/80 rounded-lg text-amber-800 shrink-0">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex items-center gap-1.5">
              <span className="font-extrabold shrink-0 text-amber-900">
                {isSearchingCitation
                  ? "Buscando cita..."
                  : citationPage
                  ? `Cita en Pág. ${citationPage}:`
                  : "Cita del chip:"}
              </span>
              <span className="truncate italic text-slate-700 text-[11px]" title={activeHighlight}>
                "{activeHighlight}"
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {citationPage && (
              <Button
                variant="outline"
                size="sm"
                onClick={scrollToCitation}
                className="h-6 px-2 text-[10px] font-extrabold bg-amber-200/60 hover:bg-amber-300/80 text-amber-900 border-amber-300 rounded-lg shadow-2xs"
              >
                Enfocar cita
              </Button>
            )}
            <button
              onClick={() => setIsHighlightVisible(false)}
              title="Ocultar resaltado"
              className="p-1 text-amber-700 hover:text-amber-900 hover:bg-amber-200/60 rounded-md transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content / Scrollable PDF Canvas Area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-auto p-4 flex flex-col items-center custom-scrollbar relative"
      >
        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50/90 backdrop-blur-xs z-30">
            <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-xl flex items-center justify-center mb-3">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
            <p className="text-xs font-bold text-slate-700">Cargando documento con PDF.js...</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Preparando páginas y resaltado de citas</p>
          </div>
        )}

        {/* Error Fallback */}
        {errorMessage && !isLoading && (
          <div className="flex flex-col items-center justify-center p-8 text-center my-auto max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h4 className="text-sm font-bold text-slate-800 mb-1">Error al visualizar PDF</h4>
            <p className="text-xs text-slate-500 mb-4">{errorMessage}</p>
            {onOpenInNewTab && (
              <Button
                onClick={onOpenInNewTab}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Abrir en nueva pestaña
              </Button>
            )}
          </div>
        )}

        {/* Rendered PDF Pages */}
        {!isLoading &&
          !errorMessage &&
          Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const dims = pageDimensions[pageNum - 1] || { width: 612, height: 792, aspectRatio: 612 / 792 };
            const scaledWidth = Math.floor(dims.width * scale);
            const scaledHeight = Math.floor(dims.height * scale);
            const isRendered = renderedPages.has(pageNum);

            return (
              <div
                key={pageNum}
                data-page-number={pageNum}
                ref={(el) => {
                  if (el) pageRefs.current.set(pageNum, el);
                  else pageRefs.current.delete(pageNum);
                }}
                className="pdf-page-container relative transition-shadow"
                style={{
                  width: `${scaledWidth}px`,
                  minHeight: `${scaledHeight}px`,
                }}
              >
                {isRendered ? (
                  <>
                    <canvas
                      ref={(el) => {
                        if (el) canvasRefs.current.set(pageNum, el);
                        else canvasRefs.current.delete(pageNum);
                      }}
                      className="block"
                    />
                    <div
                      ref={(el) => {
                        if (el) textLayerRefs.current.set(pageNum, el);
                        else textLayerRefs.current.delete(pageNum);
                      }}
                      className="textLayer"
                    />
                  </>
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center bg-slate-50 text-slate-400 text-xs font-medium"
                    style={{ height: `${scaledHeight}px` }}
                  >
                    <span>Página {pageNum}</span>
                  </div>
                )}

                {/* Page number badge indicator */}
                <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-slate-900/60 text-white text-[10px] font-bold backdrop-blur-xs pointer-events-none z-10 opacity-70">
                  {pageNum}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default PdfJsViewer;
